// Yale SOM-specific prompts for MBA admissions evaluation
import { GMAT_CONVERSION_TABLE, GRE_CONVERSION_TABLE, GPA_CONVERSION_TABLE } from '../conversionTables.js';

export const YALE_SOM_PROMPTS = {
  name: "Yale SOM",
  fullName: "Yale School of Management",
  
  schoolContext: `
${GMAT_CONVERSION_TABLE}

${GRE_CONVERSION_TABLE}

${GPA_CONVERSION_TABLE}

YALE SOM CONTEXT & CULTURE:
Mission: "Yale SOM educates leaders for business and society" - integrating profit with purpose

Core Philosophy:
- "Business and Society" approach - unique among top MBA programs
- Integrated curriculum connecting business decisions to societal impact
- Raw Case Method + experiential learning
- Small class size (347 students) fostering close community
- New Haven location with access to NYC (90 minutes)

Key Characteristics:
- Average age 28, 4.5 years work experience
- 49% women, strong focus on gender diversity
- 44% international students from 60+ countries
- Median GMAT 730 Classic/675 Focus (96.7 percentile) - range 690-760 Classic/635-715 Focus
- Average GPA 3.7 on 4.0 scale
- 56% of US students are students of color
- Strong emphasis on social impact, sustainability, ethics
- Integrated curriculum: no traditional majors, all students take same core
- Global Network for Advanced Management (29 top schools worldwide)
- Career outcomes: 40% Consulting, 25% Finance, 20% Tech, 15% Other
- Average salary $175,000 with 95% job placement rate

DIVERSITY & REPRESENTATION PRIORITIES:
Based on Yale SOM MBA Class of 2026 data (347 students):
- Gender: 39% women (actively working to increase female representation)
- US Students (52% of class): 56% are students of color, 16% are underrepresented students of color
- International Students (48% of class): From 42 countries with no dominant nationality
- GEOGRAPHIC FOCUS: As a US-based program, Americans comprise majority, but significant international diversity
- UNDERREPRESENTED GROUPS: African students, students from conflict-affected regions, underrepresented minorities
- WELL-REPRESENTED: While specific nationality data not published, likely includes significant representation from major business school feeder countries
- Professional diversity: 80% private sector, 20% nonprofit/public sector - seeking diverse backgrounds
- Socioeconomic diversity: 20% first-generation college students, strong financial aid program
- Mission-driven: Emphasis on candidates committed to "business and society"

SPECIAL DIVERSITY CONSIDERATIONS:
- Candidates from underrepresented regions and conflict-affected areas bring valuable perspectives
- Look for unique demographic intersections that add meaningful diversity to the class
- Value experiences that demonstrate resilience, global awareness, and cross-cultural competence
- Emerging markets and underrepresented nationalities contribute to Yale SOM's global mission

What Yale SOM Values in Candidates:
- Mission alignment with "business and society" philosophy
- Leadership with social impact focus
- Collaborative rather than competitive mindset
- Analytical rigor combined with humanistic perspective
- Global citizenship and cultural competence
- Entrepreneurial thinking with ethical foundation
- Career goals that integrate profit with purpose
- Evidence of making positive impact in current role
- Intellectual curiosity and academic excellence
- Community involvement and service orientation

EVALUATION INSTRUCTIONS:
- Use conversion tables above to standardize GMAT/GRE/GPA assessments
- AUTO-DETECT TEST TYPE: GMAT Classic (>340, ends in 0), GMAT Focus (>340, ends in 5), GRE (≤340)
- GMAT Focus: Convert to GMAT Classic equivalent (e.g., "GMAT Focus 675 equivalent to GMAT Classic 730")
- GRE: Convert to GMAT Classic equivalent (e.g., "GRE 332 equivalent to GMAT Classic 720")
- GMAT Classic: Report as-is (e.g., "GMAT Classic 730")
- GPA: Follow conversion guidelines - only convert if clearly NOT on 4.0 scale (e.g., "8.6/10" or "75/100"). DO NOT convert if already on 4.0 scale (e.g., "3.3" or "3.3/4")
- Always state converted scores in your evaluation for transparency and mention detected test type
- DO NOT mention percentiles - only report GMAT Classic equivalents
- Compare to Yale SOM benchmarks: GMAT Classic 730, GPA 3.7/4.0
- Keep conversation natural, avoid repetitive language about the same concern
`,

  step1EvaluationPrompt: `
STEP 1: DEMOGRAPHICS & ACADEMICS EVALUATION FOR YALE SOM

Provide a comprehensive evaluation covering: (a) where academics stand vs school benchmark, (b) race/nationality uniqueness and under/overrepresentation, (c) any additional degrees/achievements that are a slight positive (look out for unique profiles like PhDs, medical doctors, or non-traditional professions - these might not necessarily be positive and will require justification).

CRITICAL TEST SCORE AUTO-DETECTION - FOLLOW THIS EXACTLY:

STEP 1: Look at the LAST DIGIT of the score:
- If score ends in 5 (705, 715, 725, 735, etc.) → This is GMAT FOCUS
- If score ends in 0 (700, 710, 720, 730, etc.) → This is GMAT CLASSIC
- If score is ≤340 (320, 325, 330, etc.) → This is GRE

STEP 2: Convert if needed:
- GMAT FOCUS → Convert to Classic using table (e.g., "GMAT Focus 725 = GMAT Classic 760")
- GMAT CLASSIC → Report as-is (e.g., "GMAT Classic 730")
- GRE → Convert to Classic using table (e.g., "GRE 330 = GMAT Classic 700")

EXAMPLES OF CORRECT DETECTION:
- Score 725: Ends in 5 → GMAT FOCUS → "GMAT Focus 725 converts to GMAT Classic 760"
- Score 730: Ends in 0 → GMAT CLASSIC → "GMAT Classic 730"
- Score 320: ≤340 → GRE → "GRE 320 converts to GMAT Classic 600"

NEVER compare Focus scores directly to benchmarks without converting first!

GPA CONVERSION RULES:
- IF GPA < 4.0 WITH NO DENOMINATOR (e.g., "3.3") → ALREADY ON 4.0 SCALE, DO NOT CONVERT
- ONLY convert if CLEARLY international scale (e.g., "8.6/10", "75/100")
- Simple format: "GPA of 8.3/10 translates to 3.32 on 4.0 scale"

DIVERSITY REALITY CHECK:
- TRADITIONAL URM (HIGH VALUE): Black/African American, Native American, Hispanic/Latino
- OVERREPRESENTED: Indians, Chinese, East Asians
- HIGH VALUE AT YALE SOM: First-gen college (20%), military veterans, rural backgrounds, non-profit experience
- MISSION-ALIGNED: Social impact backgrounds, public service, business-and-society focus

OUTPUT FORMAT:
Write a natural, detailed evaluation that includes:
1. A short summary of academic performance vs Yale SOM benchmarks (GMAT Classic 730, GPA 3.7)
2. Assessment of nationality/race representation and diversity value
3. Discussion of any additional academic credentials (only if they exist and are relevant)
4. Clear, conversational analysis - not bullet points or templates

End with: "Consensus Rating: [1.0-5.0 in 0.5 increments]"

Be analytical but natural. Most candidates rate 2.5-3.5. Strong academics + meaningful diversity = 4.0-5.0.
`,

  step2EvaluationPrompt: `
STEP 2: WORK EXPERIENCE & RESUME EVALUATION FOR YALE SOM

Provide a comprehensive evaluation covering:
- CONTENT: Focus on how long the person has worked, promotions, career gaps, pedigree and brand of the employer coupled with the role (eg IT support in McKinsey is not good even though McKinsey is a good brand name). How does the candidate's work experience align with Yale SOM's culture, employers, etc.
- FORMAT: See if the resume bullets are following the STAR format or not, whether the numbers are realistic, if there are jarring format errors or grammatical mistakes.

CRITICAL: FOCUS ONLY ON WORK EXPERIENCE CONTENT - DO NOT MENTION ACADEMICS, GPA, DIVERSITY, OR DEMOGRAPHICS

YALE SOM CONTEXT:
- Average 4.5 years experience, age 28
- Values: "Business and Society" mission, collaborative culture, leadership with purpose
- Strong preference for top-tier employers and leadership progression
- Employment patterns: 40% consulting, 25% finance, 20% tech, 15% other
- Small class size (347) means each candidate shapes community significantly

OUTPUT FORMAT:
Write a natural, detailed evaluation that includes:

1. **Short Summary**: Brief overview of career progression and fit with Yale SOM profile

2. **Strengths** (ideally 2 each, but could be more or less depending on the resume): 
   Focus on genuine work experience strengths like employer quality, role progression, leadership experience, quantified achievements, mission-aligned experience

3. **Weaknesses** (ideally 2 each, but could be more or less depending on the resume):
   Focus on genuine concerns like short tenures, lateral moves, lack of progression, weak roles, unclear responsibilities, missing leadership

4. **Actionable Insights** (about 1-4 depending on the resume):
   MUST quote specific resume bullets and provide improved versions. Examples:
   - Quote: "Led team to improve processes" → Issue: No quantification → Improved: "Led 8-person team to improve processes, reducing costs by 15% and saving $200K annually"
   - Quote: "Managed various client relationships" → Issue: Vague language → Improved: "Managed 12 Fortune 500 client relationships worth $5M+ in annual revenue"

Write in a conversational, natural tone - not bullet points or templates. Make specific references to actual resume content.

End with: "Consensus Rating: [1.0-5.0 in 0.5 increments]"

Most candidates rate 2.5-3.5. Strong progression + leadership evidence + clear presentation = 4.0-5.0.
`,

  step3EvaluationPrompt: `
STEP 3: ESSAYS & GOALS EVALUATION FOR YALE SOM

The AI should look for whether the essays answer the questions that have been asked, how realistic the answers are, does the essay seem coherent with the candidate's resume, academics, demographics, whether the essay meets the qualities that the school is looking for. How coherent are the post-MBA short/long term goals with candidate's previous experience and the school's employment reputation.

This is NOT the final evaluation - focus solely on essay content and career goals.

YALE SOM CONTEXT & VALUES:
- "Business and Society" mission - integrating profit with purpose
- Collaborative culture vs competitive approaches
- Integrated curriculum with no traditional majors
- Small class size (347) fostering close community
- Employment patterns: 40% consulting, 25% finance, 20% tech, 15% other
- Values authentic leadership alongside strong credentials

OUTPUT FORMAT:
Write a natural, detailed evaluation that includes:

1. **Short Summary**: Overall assessment of essay quality and goal coherence

2. **Strengths** (ideally 2 each, but could be more or less depending on the essays):
   Focus on strong aspects like clear answers to questions, compelling narratives, realistic goals, good writing quality, evidence of Yale SOM values, authentic mission alignment

3. **Weaknesses** (ideally 2 each, but could be more or less depending on the essays):
   Focus on concerns like unclear answers, unrealistic goals, poor writing, lack of school fit, weak connections between components, forced social impact language

4. **Actionable Insights**:
   Specific suggestions such as "while the essays show X and Y qualities, they do not show Z quality." Examples:
   - "While essays demonstrate analytical skills, they don't show collaborative leadership that Yale SOM values"
   - "Essays don't clearly connect previous consulting experience to stated social impact goals"
   - "Social impact language feels forced rather than authentic"
   - "Goals don't align with Yale SOM's employment patterns or collaborative culture"

CRITICAL: In the output it is very important to show that AI has made connections between resume, essays, and the qualities school looks for and school's culture.

Write in a conversational, natural tone. Make specific references to actual essay content and how it connects (or doesn't connect) to the candidate's background and Yale SOM's expectations.

End with: "Consensus Rating: [1.0-5.0 in 0.5 increments]"

Most candidates rate 2.5-3.5. Excellent writing + realistic goals + authentic Yale SOM fit + strong coherence = 4.0-5.0.
`,

  step4EvaluationPrompt: `
STEP 4: FINAL HOLISTIC EVALUATION & ADMISSION PROBABILITY

Provide a comprehensive final assessment integrating all evaluation components for Yale SOM MBA admission.

**OVERALL CANDIDATE SUMMARY:**
[Brief 2-3 sentence overview synthesizing the candidate's profile and fit]

**COMPONENT PERFORMANCE RECAP:**
- **Academics (Step 1)**: [Rating from 1-5] - [Brief strength/weakness summary]
- **Work Experience (Step 2)**: [Rating from 1-5] - [Brief strength/weakness summary]  
- **Essays & Goals (Step 3)**: [Rating from 1-5] - [Brief strength/weakness summary]

**YALE SOM-SPECIFIC FIT ASSESSMENT:**
- **Business & Society Integration**: [Evidence of authentic mission alignment, not forced language]
- **Collaborative Leadership**: [Demonstration of teamwork-oriented vs competitive leadership style]
- **Integrated Thinking**: [Ability to connect business decisions with societal impact]
- **Community Contribution**: [How candidate would contribute to Yale SOM's small, tight-knit class of 347]

**KEY STRENGTHS (3-4 items):**
- [Specific standout qualities that support admission]

**KEY CONCERNS (2-3 items):**
- [Specific areas of weakness or risk factors]

**ADMISSION PROBABILITY ASSESSMENT:**

**ACCEPT PROBABILITY: [X]%**
[Select appropriate range: 65-80% (Strong Accept), 50-65% (Likely Accept), 35-50% (Competitive Accept)]

**WAITLIST PROBABILITY: [Y]%**
[Select appropriate range: 15-35% depending on competitiveness]

**REJECT PROBABILITY: [Z]%**
[Select appropriate range: 5-50% depending on gaps]

**KEY REASONING FOR PROBABILITY:**
1. **[Primary Factor]**: [Most important factor driving the assessment]
2. **[Secondary Factor]**: [Second most important consideration]
3. **[Tertiary Factor]**: [Third key factor in decision]

**FINAL RECOMMENDATION:**
[ACCEPT/WAITLIST/REJECT]

**Final Committee Rating: [1.0-5.0 in 0.5 increments]**

**DECISION RATIONALE:**
[2-3 sentences explaining the final decision based on Yale SOM's standards, mission alignment, and the candidate's overall profile]

Note: Probabilities should reflect realistic Yale SOM admission rates (~23% overall acceptance rate) and be consistent with component ratings. Consider the impact of small class size on selectivity.
`
};

// School information for display
export const YALE_SOM_INFO = {
  name: "Yale SOM",
  fullName: "Yale School of Management",
  description: "Educating Leaders for Business and Society",
  locations: ["New Haven, CT"],
  programLength: "2 years",
  classSize: "347 students",
  averageGMAT: 730,
  averageGPA: 3.7,
  averageAge: 28,
  femalePercentage: 49,
  internationalPercentage: 44,
  website: "https://som.yale.edu"
}; 